import { useState } from 'react';
import useCollectionCard from '../hooks/useCollectionCard';
import CollectionStatus from './CollectionStatus';
import DSBox from '../../../design-system/DSBox';
import DSRadio from '../../../design-system/DSRadio';
import {
  DSCard,
  DSCardActions,
  DSCardContent,
} from '../../../design-system/Card';
import {
  DSTitleTypography,
  DSTypography,
} from '../../../design-system/Typography';
import MenuButton from '../../../components/MenuButton/MenuButton';
import WarningDialog from '../../../components/WarningDialog';
import ErrorDialog from '../../../components/ErrorDialog';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { useTheme } from '@mui/material';
import DSValueTypography from '../../../design-system/Typography/DSValueTypography';
import {
  CollectionReviewStates,
  CollectionType,
  CollectionUnion,
} from '../../../api/collections';
import { DSCollectionCardMedia } from '../../../design-system/Collection';

export type CollectionCardProps = {
  collection: CollectionUnion;
  handleDeleteCollection?: (collectionId: string) => void;
  isMenuHidden?: boolean;
  isSelected?: boolean;
  onSelect?: (collection: CollectionUnion) => void;
};

const CollectionCard = ({
  collection,
  isMenuHidden,
  handleDeleteCollection,
  isSelected,
  onSelect,
}: CollectionCardProps) => {
  const {
    menuButtonItemsList,
    onClickCard,
    warningDialogOpen,
    isNameTaken,
    handleWarningConfirm,
    handleWarningClose,
    warningMessage,
    nameWarningNote,
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
  } = useCollectionCard(collection, handleDeleteCollection);
  const { spacing, palette } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (onSelect) {
      onSelect(collection);
    } else {
      onClickCard(collection);
    }
  };

  const CollectionCover = () => {
    if (!collection.coverUrl)
      return (
        <DSCollectionCardMedia
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: spacing(1),
          }}
        >
          <ImageOutlinedIcon
            sx={{
              height: spacing(6),
              width: spacing(6),
              color: palette.action.active,
            }}
          />
          <DSValueTypography
            fontSize={spacing(2)}
            color={palette.action.active}
          >
            Collection Cover
          </DSValueTypography>
        </DSCollectionCardMedia>
      );
    return <DSCollectionCardMedia image={collection.coverUrl} />;
  };

  return (
    <DSBox
      sx={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {onSelect && (isHovered || isSelected) && (
        <DSBox
          sx={{
            position: 'absolute',
            top: spacing(1),
            right: spacing(1),
            zIndex: 10,
          }}
        >
          <DSRadio
            checked={!!isSelected}
            sx={{
              color: isSelected ? '#FFF' : 'rgba(255, 255, 255, 0.56)',
              '& .MuiSvgIcon-root': {
                color: isSelected ? '#FFF' : 'rgba(255, 255, 255, 0.56)',
              },
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          />
        </DSBox>
      )}
      <DSCard
        sx={{
          padding: spacing(2),
          width: spacing(40),
          height: spacing(59),
          borderRadius: spacing(4),
          backgroundColor: isSelected
            ? palette.paper.elevation2
            : palette.paper.elevation5,
          border: isSelected ? '3px solid #FFF' : '3px solid transparent',
          transition: 'all 0.2s',
          boxShadow:
            '0px 1px 14px 0px rgba(0, 0, 0, 0.12), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)',
        }}
      >
        <DSBox
          onClick={handleClick}
          sx={{ justifyContent: 'center', display: 'flex', cursor: 'pointer' }}
        >
          <CollectionCover />
        </DSBox>
        <DSBox>
          <DSCardContent
            sx={{
              textAlign: 'left',
              paddingBottom: 0,
              paddingX: spacing(1),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: spacing(12),
            }}
          >
            <DSTitleTypography
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                width: '100%',
              }}
            >
              {collection.name}
            </DSTitleTypography>

            <DSTypography variant="body1">
              Last Update{' '}
              {new Date(collection.updatedAt)
                .toLocaleDateString(undefined, {
                  year: '2-digit',
                  month: '2-digit',
                  day: '2-digit',
                })
                .replace(/\//g, '.')}
            </DSTypography>
          </DSCardContent>
          <DSCardActions
            sx={{ justifyContent: 'space-between', paddingTop: 0 }}
          >
            <CollectionStatus
              state={
                collection.type === CollectionType.Draft
                  ? collection?.reviewStatus?.state
                  : CollectionReviewStates.Approved
              }
              marketplace={collection.marketplace}
            />

            {!isMenuHidden && <MenuButton menuItems={menuButtonItemsList} />}
          </DSCardActions>
        </DSBox>
      </DSCard>
      <WarningDialog
        warningMessage={warningMessage}
        open={warningDialogOpen}
        handleConfirmationClick={handleWarningConfirm}
        handleClose={handleWarningClose}
        isConfirmDisabled={isNameTaken}
        warningNote={nameWarningNote}
      />
      <ErrorDialog
        errorTitle="Please complete your profile"
        errorReason={'Profile is missing some required fields'}
        isErrorDialogOpen={profileIncompleteErrorDialogOpen}
        handleClose={() => setProfileIncompleteErrorDialogOpen(false)}
      />
    </DSBox>
  );
};

export default CollectionCard;
